// ============================================================
// Base path for the Shopify App Proxy. Shopify signs these requests and
// forwards them to the backend, so the backend address never reaches the
// browser. Declared at top level because API_BASE below is scoped to the
// OrderDelivery IIFE, while order creation lives outside it.
// ============================================================
const BRUSHME_API = '/apps/api';

// ============================================================
// OrderFormStrings — Liquid's `| t` HTML-escapes translations, so an apostrophe
// arrives here as `&#39;` and shows up literally in textContent. Decode once,
// before anything reads the strings. Keys ending in `_html` are not escaped by
// Shopify and pass through unchanged.
// ============================================================
(function () {
    var S = window.OrderFormStrings;
    if (!S) return;

    var ta = document.createElement('textarea');
    Object.keys(S).forEach(function (key) {
        var val = S[key];
        if (typeof val !== 'string' || val.indexOf('&') === -1) return;
        ta.innerHTML = val;
        S[key] = ta.value;
    });
})();

// ============================================================
// OrderCart — cart utilities (load, clear, render items, price, promo state)
// ============================================================
window.OrderCart = (function () {
    function formatPrice(cents) {
        return (cents / 100).toLocaleString('uk-UA', { style: 'currency', currency: 'UAH' });
    }

    function escapeHtml(str) {
        return String(str).replace(/[&<>"']/g, ch => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[ch]));
    }

    function renderItemProperties(properties) {
        if (!properties) return '';
        const entries = Object.entries(properties).filter(([key, value]) =>
            value && !String(key).startsWith('_')
        );
        if (entries.length === 0) return '';

        const photos = entries.filter(([key]) => key.toLowerCase().includes('photo_url_'));
        const others = entries.filter(([key]) => !key.toLowerCase().includes('photo_url_'));

        let html = '';

        if (others.length > 0) {
            html += '<dl class="of-cart-item__props">';
            html += others.map(([key, value]) => {
                const safeKey = escapeHtml(key);
                const isUpload = String(value).includes('/uploads/');
                const valueHtml = isUpload
                    ? `<a href="${escapeHtml(value)}" target="_blank" rel="noopener">${escapeHtml(String(value).split('/').pop())}</a>`
                    : escapeHtml(value);
                return `<div class="of-cart-item__prop"><dt>${safeKey}:</dt><dd>${valueHtml}</dd></div>`;
            }).join('');
            html += '</dl>';
        }

        if (photos.length > 0) {
            html += '<div class="of-cart-item__photos">';
            html += photos.map(([, url], i) => `
                <a href="${escapeHtml(url)}" class="of-cart-item__photo" target="_blank" rel="noopener" aria-label="Завантажене фото ${i + 1}">
                    <img src="${escapeHtml(url)}" alt="Завантажене фото ${i + 1}" loading="lazy" width="64" height="64">
                </a>
            `).join('');
            html += '</div>';
        }

        return html;
    }

    function renderItems(cart, containerEl) {
        if (!containerEl) return;

        if (!cart.items || cart.items.length === 0) {
            const S = window.OrderFormStrings || {};
            containerEl.innerHTML = `<p style="font-size:1.3rem;opacity:.6">${S.cartEmpty || ''}</p>`;
            return;
        }

        containerEl.innerHTML = cart.items.map(item => `
            <div class="of-cart-item">
                <img class="of-cart-item__img" src="${item.image}" alt="${escapeHtml(item.product_title)}">
                <div class="of-cart-item__info">
                    <div class="of-cart-item__title">${escapeHtml(item.product_title)}</div>
                    <div class="of-cart-item__qty">${item.quantity} ${(window.OrderFormStrings || {}).qtyUnit || ''}</div>
                    ${renderItemProperties(item.properties)}
                </div>
                <div class="of-cart-item__price">${formatPrice(item.final_line_price)}</div>
            </div>
        `).join('');
    }

    function renderPriceSummary(cart, summaryEl) {
        if (!summaryEl) return;

        const S = window.OrderFormStrings || {};
        const rows = [{ label: S.totalLabel || '', value: formatPrice(cart.original_total_price) }];
        if (cart.total_discount > 0) {
            rows.push({ label: S.discountLabel || '', value: '−' + formatPrice(cart.total_discount) });
        }

        summaryEl.innerHTML = rows.map(r =>
            `<div class="of-price-row"><span>${r.label}</span><strong>${r.value}</strong></div>`
        ).join('') + `
            <div class="of-price-total">
                <span class="of-price-total__label">${S.totalSummaryLabel || ''}</span>
                <span class="of-price-total__value">${formatPrice(cart.total_price)}</span>
            </div>
        `;
    }

    function getAppliedDiscountCode(cart) {
        // Основне джерело — discount_codes (працює і для shipping-знижок)
        const codes = cart.discount_codes || [];
        for (let i = codes.length - 1; i >= 0; i--) {
            if (codes[i].applicable !== false) return codes[i].code;
        }

        // Фолбек — cart_level_discount_applications
        const apps = cart.cart_level_discount_applications || cart.discount_applications || [];
        for (let i = 0; i < apps.length; i++) {
            const app = apps[i];
            if (app.type !== 'discount_code') continue;
            const hasAllocation = (app.total_allocated_amount || 0) > 0;
            const isShipping = app.target_type === 'shipping_line';
            if (hasAllocation || isShipping) return app.title;
        }
        return null;
    }

    function renderFreeShippingGoal(cart) {
        const wrap = document.getElementById('of-shipping-goal');
        if (!wrap) return;
        const min = Number(wrap.dataset.minimum);
        if (!min || min <= 0) { wrap.hidden = true; return; }

        const applicableCodes = (cart.discount_codes || []).filter(d => d.applicable === true);
        const hasAllocations = (cart.cart_level_discount_applications || []).length > 0 || (cart.total_discount || 0) > 0;
        const hasShippingDiscount = applicableCodes.length > 0 && !hasAllocations;

        const totalUah = (cart.total_price || 0) / 100;
        const remainingUah = hasShippingDiscount ? 0 : min - totalUah;
        const percent = hasShippingDiscount ? 100 : Math.min((totalUah / min) * 100, 100);

        const text = document.getElementById('of-shipping-goal-text');
        const fill = document.getElementById('of-shipping-goal-fill');

        const S = window.OrderFormStrings || {};
        if (remainingUah <= 0) {
            wrap.classList.add('of-shipping-goal--done');
            if (text) text.innerHTML = S.freeShippingDone || '';
            if (fill) fill.style.width = '100%';
        } else {
            wrap.classList.remove('of-shipping-goal--done');
            if (text) text.innerHTML = (S.freeShippingRemaining || '').replace('{{ amount }}', formatPrice(remainingUah * 100));
            if (fill) fill.style.width = `${percent}%`;
        }
        wrap.hidden = false;
    }

    function renderCodSubtitle(cart) {
        const el = document.getElementById('of-cod-subtitle');
        if (!el) return;
        const S = window.OrderFormStrings || {};
        const totalUah = (cart.total_price || 0) / 100;
        const commission = Math.round(20 + totalUah * 0.005);
        el.textContent = (S.codCommission || '').replace('{{ amount }}', commission + ' грн');
    }

    function renderPromoState(cart, inputStateEl, appliedStateEl, codeEl) {
        if (!inputStateEl || !appliedStateEl || !codeEl) return;
        const code = getAppliedDiscountCode(cart);
        if (code) {
            inputStateEl.hidden = true;
            appliedStateEl.hidden = false;
            codeEl.textContent = code;
        } else {
            inputStateEl.hidden = false;
            appliedStateEl.hidden = true;
            codeEl.textContent = '';
        }
    }

    async function load() {
        return fetch('/cart.js', {
            headers: { 'Accept': 'application/json' }
        }).then(r => r.json());
    }

    async function clear() {
        await fetch('/cart/clear.js', {
            method: 'POST',
            headers: { 'Accept': 'application/json' }
        });
    }

    return {
        load, clear, formatPrice,
        renderItems, renderPriceSummary, renderPromoState, renderFreeShippingGoal, renderCodSubtitle,
        getAppliedDiscountCode
    };
})();


// ============================================================
// OrderDelivery — autocomplete для Нової Пошти та Укрпошти
// ============================================================
window.OrderDelivery = (function () {
    const API_BASE = BRUSHME_API;

    const state = {
        selectedCity: null,
        currentCarrier: 'nova',
        deliveryToAddress: false,
    };

    // ─── Debounce ───
    function debounce(fn, delay) {
        let timer;
        return function () {
            const args = arguments;
            const ctx = this;
            clearTimeout(timer);
            timer = setTimeout(() => fn.apply(ctx, args), delay);
        };
    }

    // ─── DOM helpers ───
    const qs = (id) => document.getElementById(id);

    function setHidden(el, val) { if (el) el.hidden = val; }

    function setDisabled(fieldEl, val) {
        if (!fieldEl) return;
        fieldEl.classList.toggle('of-autocomplete--disabled', val);
        fieldEl.querySelectorAll('input:not([type="hidden"])').forEach(inp => {
            inp.disabled = val;
        });
    }

    function setHiddenField(name, value) {
        const el = document.querySelector(`input[type="hidden"][name="${name}"]`);
        if (el) el.value = value || '';
    }

    function setSpinner(fieldId, loading) {
        const field = qs(fieldId);
        if (!field) return;
        const spinner = field.querySelector('.of-autocomplete__spinner');
        if (spinner) spinner.classList.toggle('is-spinning', loading);
    }

    function showFieldError(errId, msg) {
        const el = qs(errId);
        if (!el) return;
        el.textContent = msg || '';
        el.hidden = !msg;
    }

    // ─── Render dropdown list ───
    function renderList(listId, items, onSelect) {
        const list = qs(listId);
        if (!list) return;
        list.innerHTML = '';

        if (!items || items.length === 0) {
            list.hidden = true;
            return;
        }

        items.forEach(item => {
            const li = document.createElement('li');
            li.className = 'of-autocomplete__item';
            li.setAttribute('role', 'option');
            li.textContent = item.label;
            li.addEventListener('mousedown', (e) => {
                e.preventDefault();
                onSelect(item);
                list.hidden = true;
                list.innerHTML = '';
            });
            list.appendChild(li);
        });

        list.hidden = false;
    }

    function closeList(listId) {
        const list = qs(listId);
        if (list) { list.hidden = true; list.innerHTML = ''; }
    }

    // ─── API ───
    function apiFetch(params) {
        const qstr = Object.keys(params)
            .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
            .join('&');
        return fetch(`${API_BASE}/delivery/search?${qstr}`).then(r => r.json());
    }

    // ─── Mapping ───
    const mapCities = (data) => (data || []).map(item =>
        ({ ref: item.value, name: item.name, label: item.name })
    );

    const mapWarehouses = (data) => (data || []).map(item => {
        const desc = item.description ? `${item.name}, ${item.description}` : item.name;
        return { ref: item.value, description: desc, label: desc };
    });

    // Для вулиць береться description, не name
    const mapStreets = (data) => (data || []).map(item =>
        ({ ref: item.value, name: item.description, label: item.description })
    );

    const mapUpBranches = (data) => (data || []).map(item => ({
        ref: item.ref,
        region: item.region,
        label: `м. ${item.region}, відділення ${item.ref}`,
    }));

    // ─── Disable/enable dependent fields ───
    function setDependentFieldsDisabled(val) {
        setDisabled(qs('of-warehouse-field'), val);
        setDisabled(qs('of-street-field'), val);
        setDisabled(qs('of-building-field'), val);
        setDisabled(qs('of-flat-field'), val);
    }

    // ─── Clear helpers ───
    function clearWarehouseValues() {
        const inp = qs('of-warehouse-input');
        if (inp) inp.value = '';
        setHiddenField('warehouse', '');
        setHiddenField('warehouseRef', '');
        closeList('of-warehouse-list');
    }

    function clearStreetValues() {
        const inp = qs('of-street-input');
        if (inp) inp.value = '';
        setHiddenField('street', '');
        setHiddenField('streetRef', '');
        closeList('of-street-list');
    }

    function clearAddressValues() {
        clearStreetValues();
        const b = qs('building');
        const f = qs('flat');
        if (b) b.value = '';
        if (f) f.value = '';
    }

    function resetNovaState() {
        state.selectedCity = null;
        const cityInp = qs('of-city-input');
        if (cityInp) cityInp.value = '';
        setHiddenField('city', '');
        setHiddenField('cityRef', '');
        clearWarehouseValues();
        clearAddressValues();
        setDependentFieldsDisabled(true);
    }

    function resetUkrState() {
        const zipInp = qs('of-upzip-input');
        if (zipInp) zipInp.value = '';
        setHiddenField('warehouseRef', '');
        setHiddenField('city', '');
        const hint = qs('of-upzip-hint');
        if (hint) { hint.textContent = ''; hint.hidden = true; }
        closeList('of-upzip-list');
    }

    // ─── City ───
    function onCitySelect(city) {
        state.selectedCity = city;
        const inp = qs('of-city-input');
        if (inp) inp.value = city.name;
        setHiddenField('city', city.name);
        setHiddenField('cityRef', city.ref);
        showFieldError('of-err-city', '');

        clearWarehouseValues();
        clearAddressValues();
        setDependentFieldsDisabled(false);

        // Одразу завантажити перші відділення
        fetchAndRenderWarehouses('');
    }

    const debouncedSearchCities = debounce((query) => {
        if (!query) { closeList('of-city-list'); return; }
        setSpinner('of-city-field', true);
        apiFetch({ type: 'np_city', query })
            .then(json => renderList('of-city-list', mapCities(json.data), onCitySelect))
            .catch(() => closeList('of-city-list'))
            .then(() => setSpinner('of-city-field', false));
    }, 350);

    // ─── Warehouse ───
    function onWarehouseSelect(w) {
        const inp = qs('of-warehouse-input');
        if (inp) inp.value = w.description;
        setHiddenField('warehouse', w.description);
        setHiddenField('warehouseRef', w.ref);
        showFieldError('of-err-warehouse', '');
    }

    function fetchAndRenderWarehouses(query) {
        if (!state.selectedCity) return;
        setSpinner('of-warehouse-field', true);
        apiFetch({ type: 'np_branch', parent_value: state.selectedCity.ref, query })
            .then(json => renderList('of-warehouse-list', mapWarehouses(json.data), onWarehouseSelect))
            .catch(() => closeList('of-warehouse-list'))
            .then(() => setSpinner('of-warehouse-field', false));
    }

    const debouncedSearchWarehouses = debounce((query) => {
        if (!state.selectedCity) return;
        fetchAndRenderWarehouses(query);
    }, 350);

    // ─── Street ───
    function onStreetSelect(s) {
        const inp = qs('of-street-input');
        if (inp) inp.value = s.name;
        setHiddenField('street', s.name);
        setHiddenField('streetRef', s.ref);
        showFieldError('of-err-street', '');
    }

    const debouncedSearchStreets = debounce((query) => {
        if (!state.selectedCity) return;
        if (!query) { closeList('of-street-list'); return; }
        setSpinner('of-street-field', true);
        apiFetch({ type: 'np_street', parent_value: state.selectedCity.ref, query })
            .then(json => renderList('of-street-list', mapStreets(json.data), onStreetSelect))
            .catch(() => closeList('of-street-list'))
            .then(() => setSpinner('of-street-field', false));
    }, 350);

    // ─── Ukrposhta ───
    function onUpBranchSelect(b) {
        setHiddenField('warehouseRef', b.ref);
        setHiddenField('city', b.region);
        const hint = qs('of-upzip-hint');
        if (hint) { hint.textContent = `м. ${b.region}`; hint.hidden = false; }
        showFieldError('of-err-upzip', '');
    }

    const debouncedSearchUpBranches = debounce((zip) => {
        if (!zip) {
            closeList('of-upzip-list');
            const hint = qs('of-upzip-hint');
            if (hint) { hint.textContent = ''; hint.hidden = true; }
            return;
        }
        setSpinner('of-upzip-field', true);
        apiFetch({ type: 'up_zip', zip })
            .then(json => renderList('of-upzip-list', mapUpBranches(json.data), onUpBranchSelect))
            .catch(() => closeList('of-upzip-list'))
            .then(() => setSpinner('of-upzip-field', false));
    }, 350);

    // ─── Carrier change ───
    function onCarrierChange(carrier) {
        state.currentCarrier = carrier;
        const isNova = carrier === 'nova';

        setHidden(qs('of-nova-fields'), !isNova);
        setHidden(qs('of-ukr-fields'), isNova);
        setHidden(qs('of-address-toggle-wrap'), !isNova);

        // COD недоступний для Укрпошти
        const codOption = qs('of-cod-option');
        if (codOption) {
            const codRadio = codOption.querySelector('input[type="radio"]');
            codOption.classList.toggle('of-carrier-chip--disabled', !isNova);
            if (codRadio) {
                codRadio.disabled = !isNova;
                if (!isNova && codRadio.checked) {
                    codRadio.checked = false;
                    const cardOnline = document.querySelector('input[name="payment"][value="card_online"]');
                    if (cardOnline) cardOnline.checked = true;
                }
            }
        }

        // Скинути тогл адресної доставки
        const toggle = qs('of-address-toggle');
        if (toggle) toggle.checked = false;
        state.deliveryToAddress = false;
        const flag = qs('of-delivery-to-address-flag');
        if (flag) flag.value = 'false';
        setHidden(qs('of-warehouse-section'), false);
        setHidden(qs('of-address-section'), true);

        resetNovaState();
        resetUkrState();
    }

    // ─── Address toggle ───
    function onToggleAddress(isAddress) {
        state.deliveryToAddress = isAddress;
        const flag = qs('of-delivery-to-address-flag');
        if (flag) flag.value = isAddress ? 'true' : 'false';

        setHidden(qs('of-warehouse-section'), isAddress);
        setHidden(qs('of-address-section'), !isAddress);

        clearWarehouseValues();
        clearAddressValues();

        // Перемикання не скидає місто — залежні поля активні якщо місто вибране
        const hasCitySelected = !!state.selectedCity;
        setDisabled(qs('of-street-field'), !hasCitySelected);
        setDisabled(qs('of-building-field'), !hasCitySelected);
        setDisabled(qs('of-flat-field'), !hasCitySelected);
    }

    // ─── Bind blur ───
    function bindBlur(inputId, listId) {
        const input = qs(inputId);
        if (!input) return;
        input.addEventListener('blur', () => {
            setTimeout(() => closeList(listId), 150);
        });
    }

    // ─── Init ───
    function init() {
        document.querySelectorAll('input[name="carrier"]').forEach(radio => {
            radio.addEventListener('change', (e) => onCarrierChange(e.target.value));
        });

        const toggle = qs('of-address-toggle');
        if (toggle) {
            toggle.addEventListener('change', (e) => onToggleAddress(e.target.checked));
        }

        const cityInput = qs('of-city-input');
        if (cityInput) {
            cityInput.addEventListener('input', (e) => {
                // Скидаємо вибір міста при ручному введенні
                if (state.selectedCity) {
                    state.selectedCity = null;
                    setHiddenField('city', '');
                    setHiddenField('cityRef', '');
                    setDependentFieldsDisabled(true);
                    clearWarehouseValues();
                    clearAddressValues();
                }
                debouncedSearchCities(e.target.value);
            });
        }
        bindBlur('of-city-input', 'of-city-list');

        const warehouseInput = qs('of-warehouse-input');
        if (warehouseInput) {
            warehouseInput.addEventListener('input', (e) => {
                setHiddenField('warehouse', '');
                setHiddenField('warehouseRef', '');
                debouncedSearchWarehouses(e.target.value);
            });
            warehouseInput.addEventListener('focus', () => {
                if (state.selectedCity) fetchAndRenderWarehouses(warehouseInput.value);
            });
        }
        bindBlur('of-warehouse-input', 'of-warehouse-list');

        const streetInput = qs('of-street-input');
        if (streetInput) {
            streetInput.addEventListener('input', (e) => {
                setHiddenField('street', '');
                setHiddenField('streetRef', '');
                debouncedSearchStreets(e.target.value);
            });
        }
        bindBlur('of-street-input', 'of-street-list');

        const upzipInput = qs('of-upzip-input');
        if (upzipInput) {
            upzipInput.addEventListener('input', (e) => {
                setHiddenField('warehouseRef', '');
                setHiddenField('city', '');
                const hint = qs('of-upzip-hint');
                if (hint) { hint.textContent = ''; hint.hidden = true; }
                debouncedSearchUpBranches(e.target.value);
            });
        }
        bindBlur('of-upzip-input', 'of-upzip-list');

        // Початковий стан — nova активна
        const novaRadio = document.querySelector('input[name="carrier"][value="nova"]');
        if (novaRadio) {
            novaRadio.checked = true;
            onCarrierChange('nova');
        }
    }

    const getState = () => state;

    function restoreFullState(saved) {
        if (!saved) return;

        if (saved.carrier === 'nova') {
            if (saved.cityRef) {
                state.selectedCity = { ref: saved.cityRef, name: saved.city || '' };
                const cityInp = qs('of-city-input');
                if (cityInp) cityInp.value = saved.cityText || saved.city || '';
                setHiddenField('city', saved.city || '');
                setHiddenField('cityRef', saved.cityRef || '');
                setDependentFieldsDisabled(false);
            }

            if (saved.deliveryToAddress === 'true') {
                state.deliveryToAddress = true;
                const flag = qs('of-delivery-to-address-flag');
                if (flag) flag.value = 'true';
                const toggle = qs('of-address-toggle');
                if (toggle) toggle.checked = true;
                setHidden(qs('of-warehouse-section'), true);
                setHidden(qs('of-address-section'), false);

                if (saved.cityRef) {
                    setDisabled(qs('of-street-field'), false);
                    setDisabled(qs('of-building-field'), false);
                    setDisabled(qs('of-flat-field'), false);
                }
                if (saved.streetRef) {
                    const streetInp = qs('of-street-input');
                    if (streetInp) streetInp.value = saved.streetText || '';
                    setHiddenField('street', saved.street || '');
                    setHiddenField('streetRef', saved.streetRef || '');
                }
                const building = qs('building');
                if (building && saved.building) building.value = saved.building;
                const flat = qs('flat');
                if (flat && saved.flat) flat.value = saved.flat;
            } else {
                if (saved.warehouseRef) {
                    const wInp = qs('of-warehouse-input');
                    if (wInp) wInp.value = saved.warehouseText || '';
                    setHiddenField('warehouse', saved.warehouse || '');
                    setHiddenField('warehouseRef', saved.warehouseRef || '');
                }
            }
        } else if (saved.carrier === 'ukr') {
            if (saved.upZip) {
                const upzip = qs('of-upzip-input');
                if (upzip) upzip.value = saved.upZip;
            }
            if (saved.warehouseRef) setHiddenField('warehouseRef', saved.warehouseRef);
            if (saved.city) setHiddenField('city', saved.city);
            if (saved.upZipHint) {
                const hint = qs('of-upzip-hint');
                if (hint) { hint.textContent = saved.upZipHint; hint.hidden = false; }
            }
        }
    }

    return { init, getState, restoreFullState };
})();


// ============================================================
// OrderFormPersist — збереження / відновлення форми через localStorage
// ============================================================
window.OrderFormPersist = (function () {
    const KEY = 'of_saved';

    function collect(form) {
        const fd = new FormData(form);
        const v  = (name) => fd.get(name) || '';
        return {
            customerName:      v('customerName'),
            phone:             v('phone'),
            email:             v('email'),
            carrier:           v('carrier'),
            payment:           v('payment'),
            note:              v('note'),
            deliveryToAddress: v('deliveryToAddress') || 'false',
            cityText:          (document.getElementById('of-city-input')      || {}).value || '',
            city:              v('city'),
            cityRef:           v('cityRef'),
            warehouseText:     (document.getElementById('of-warehouse-input') || {}).value || '',
            warehouse:         v('warehouse'),
            warehouseRef:      v('warehouseRef'),
            streetText:        (document.getElementById('of-street-input')    || {}).value || '',
            street:            v('street'),
            streetRef:         v('streetRef'),
            building:          v('building'),
            flat:              v('flat'),
            upZip:             v('upZip'),
            upZipHint:         (document.getElementById('of-upzip-hint')      || {}).textContent || '',
        };
    }

    function save(form) {
        try { localStorage.setItem(KEY, JSON.stringify(collect(form))); } catch (_) {}
    }

    function restore(form) {
        let saved;
        try { saved = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (_) { return; }
        if (!saved) return;

        ['customerName', 'phone', 'email', 'note'].forEach(name => {
            const el = form.querySelector(`[name="${name}"]`);
            if (el && saved[name]) el.value = saved[name];
        });

        if (saved.carrier) {
            const radio = form.querySelector(`input[name="carrier"][value="${saved.carrier}"]`);
            if (radio && !radio.checked) {
                radio.checked = true;
                radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }

        OrderDelivery.restoreFullState(saved);

        if (saved.payment) {
            const payRadio = form.querySelector(`input[name="payment"][value="${saved.payment}"]`);
            if (payRadio && !payRadio.disabled) payRadio.checked = true;
        }
    }

    return { save, restore };
})();


// ============================================================
// Page init: cart render, promo UI, delivery autocomplete, form submit
// ============================================================
document.addEventListener('DOMContentLoaded', () => {
    const form           = document.getElementById('custom-order-form');
    const resultDiv      = document.getElementById('order_result');
    const cartItemsEl    = document.getElementById('of-cart-items');
    const priceSummaryEl = document.getElementById('of-price-summary');
    const promoInput     = document.getElementById('of-promo-input-state');
    const promoApplied   = document.getElementById('of-promo-applied-state');
    const promoCodeEl    = document.getElementById('of-promo-applied-code');

    if (!form) return;

    // ─── Delivery autocomplete ───
    OrderDelivery.init();

    // ─── Cart + promo state ───
    const pagePreloader = document.getElementById('of-page-preloader');

    let cachedCart = null;
    OrderCart.load().then(cart => {
        if (!cart.items || cart.items.length === 0) {
            location.href = '/cart';
            return;
        }

        cachedCart = cart;
        OrderCart.renderItems(cart, cartItemsEl);
        OrderCart.renderPriceSummary(cart, priceSummaryEl);
        OrderCart.renderPromoState(cart, promoInput, promoApplied, promoCodeEl);
        OrderCart.renderFreeShippingGoal(cart);
        OrderCart.renderCodSubtitle(cart);

        if (pagePreloader) pagePreloader.hidden = true;
        if (form) form.hidden = false;

        OrderFormPersist.restore(form);

        let saveTimer;
        form.addEventListener('input',  () => { clearTimeout(saveTimer); saveTimer = setTimeout(() => OrderFormPersist.save(form), 400); });
        form.addEventListener('change', () => { clearTimeout(saveTimer); saveTimer = setTimeout(() => OrderFormPersist.save(form), 400); });
    });

    function refreshCartUI(cart) {
        cachedCart = cart;
        OrderCart.renderItems(cart, cartItemsEl);
        OrderCart.renderPriceSummary(cart, priceSummaryEl);
        OrderCart.renderPromoState(cart, promoInput, promoApplied, promoCodeEl);
        OrderCart.renderFreeShippingGoal(cart);
        OrderCart.renderCodSubtitle(cart);
    }

    // ─── Promo: apply via /cart/update.js (no page reload) ───
    const applyBtn  = form.querySelector('.of-promo__btn');
    const codeInput = document.getElementById('discount_code');
    const promoErr  = document.getElementById('of-promo-error');

    function setPromoError(msg) {
        if (!promoErr) return;
        promoErr.textContent = msg || '';
        promoErr.hidden = !msg;
    }

    async function applyDiscountCode() {
        const code = (codeInput.value || '').trim();
        if (!code) return;

        applyBtn.disabled = true;
        applyBtn.classList.add('is-loading');
        setPromoError('');
        try {
            const resp = await fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ discount: code }),
            });
            const data = await resp.json();

            // HTTP-помилка від Shopify
            if (!resp.ok || data.status) {
                setPromoError(data.description || data.message || (window.OrderFormStrings || {}).promoInvalid || '');
                return;
            }

            // Перевіряємо останній елемент discount_codes — applicable:false означає недійсний
            const codes = data.discount_codes || [];
            const last = codes[codes.length - 1];
            if (last && last.applicable === false) {
                setPromoError(`Промокод «${last.code}» не дійсний або не виконані умови його застосування`);
                return;
            }

            refreshCartUI(data);
            codeInput.value = '';
        } catch (err) {
            console.error('Apply discount failed:', err);
            setPromoError((window.OrderFormStrings || {}).promoFailed || '');
        } finally {
            applyBtn.disabled = false;
            applyBtn.classList.remove('is-loading');
        }
    }

    if (applyBtn && codeInput) {
        applyBtn.addEventListener('click', applyDiscountCode);

        // Enter у полі промокоду — застосовує код (не сабмітить форму замовлення)
        codeInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                applyDiscountCode();
            }
        });

        // Очищати помилку при ручному редагуванні поля
        codeInput.addEventListener('input', () => setPromoError(''));
    }

    // ─── Promo: remove (no page reload) ───
    const removeBtn = document.getElementById('of-promo-remove');
    if (removeBtn) {
        removeBtn.addEventListener('click', async () => {
            removeBtn.disabled = true;
            removeBtn.classList.add('is-loading');
            try {
                await fetch('/cart/update.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ discount: '' })
                });
                const cart = await OrderCart.load();
                refreshCartUI(cart);
            } catch (err) {
                console.error('Remove discount failed:', err);
            } finally {
                removeBtn.disabled = false;
                removeBtn.classList.remove('is-loading');
            }
        });
    }

    // ─── Form submit ───
    function showResult(message, type) {
        if (!resultDiv) return;
        resultDiv.textContent = message;
        resultDiv.className = `of-result of-result--${type} is-visible`;
    }

    function showFieldError(errId, msg) {
        const el = document.getElementById(errId);
        if (!el) return;
        el.textContent = msg;
        el.hidden = !msg;
    }

    function clearAllErrors() {
        form.querySelectorAll('.of-field-error').forEach(el => {
            el.textContent = '';
            el.hidden = true;
        });
    }

    function validateForm(fd) {
        let valid = true;

        const S = window.OrderFormStrings || {};
        if (!(fd.get('customerName') || '').trim()) {
            showFieldError('of-err-customerName', S.fieldRequired || '');
            valid = false;
        }
        if (!(fd.get('phone') || '').trim()) {
            showFieldError('of-err-phone', S.fieldRequired || '');
            valid = false;
        }

        const carrier = fd.get('carrier');
        const isUkr   = carrier === 'ukr';
        const isAddr  = fd.get('deliveryToAddress') === 'true';

        if (isUkr) {
            if (!fd.get('warehouseRef')) {
                showFieldError('of-err-upzip', S.selectWarehouse || '');
                valid = false;
            }
        } else {
            if (!fd.get('city')) {
                showFieldError('of-err-city', S.fieldRequired || '');
                valid = false;
            }
            if (isAddr) {
                if (!fd.get('streetRef')) {
                    showFieldError('of-err-street', S.fieldRequired || '');
                    valid = false;
                }
                if (!(fd.get('building') || '').trim()) {
                    showFieldError('of-err-building', S.fieldRequired || '');
                    valid = false;
                }
            } else {
                if (!fd.get('warehouseRef')) {
                    showFieldError('of-err-warehouse', S.fieldRequired || '');
                    valid = false;
                }
            }
        }

        return valid;
    }

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        clearAllErrors();

        const fd = new FormData(form);

        if (!validateForm(fd)) {
            const firstErr = form.querySelector('.of-field-error:not([hidden])');
            if (firstErr) firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        showResult((window.OrderFormStrings || {}).creatingOrder || '', 'loading');

        try {
            const carrier = fd.get('carrier');
            const isUkr   = carrier === 'ukr';
            const isAddr  = fd.get('deliveryToAddress') === 'true';

            const nameParts = (fd.get('customerName') || '').trim().split(/\s+/);
            const lastName  = nameParts[0] || '';
            const firstName = nameParts.slice(1).join(' ') || '';

            let address1 = '';
            if (isUkr) {
                address1 = fd.get('city') || '';
            } else if (isAddr) {
                const flat = (fd.get('flat') || '').trim();
                address1 = [
                    fd.get('street'),
                    fd.get('building'),
                    flat ? `${(window.OrderFormStrings || {}).flatAbbr || ''} ${flat}` : ''
                ].filter(Boolean).join(', ');
            } else {
                address1 = fd.get('warehouse') || '';
            }

            const shippingAddress = {
                first_name: firstName,
                last_name:  lastName,
                address1,
                city:    fd.get('city') || '',
                zip:     '',
                country: 'UA',
                phone:   fd.get('phone'),
            };

            const cart  = cachedCart || await OrderCart.load();
            const attrs = cart.attributes || {};

            const payload = {
                financial_status:    'pending',
                carrier,
                delivery_to_address: isAddr,
                payment_method:      fd.get('payment'),
                note:                (fd.get('note') || '').trim(),
                email:               (fd.get('email') || '').trim(),
                city:                fd.get('city')         || '',
                city_ref:            fd.get('cityRef')      || '',
                warehouse:           fd.get('warehouse')    || '',
                warehouse_ref:       fd.get('warehouseRef') || '',
                street:              fd.get('street')       || '',
                street_ref:          fd.get('streetRef')    || '',
                building:            fd.get('building')     || '',
                flat:                fd.get('flat')         || '',
                up_zip:              fd.get('upZip')        || '',
                cart,
                shipping_address:    shippingAddress,
                billing_address:     shippingAddress,
                note_attributes: [
                    { name: 'landing_page',  value: attrs.src_ft_landing      || '' },
                    { name: 'orig_referrer', value: attrs.src_ft_referrer     || '' },
                    { name: 'lt_source',     value: attrs.src_lt_utm_source   || '' },
                    { name: 'lt_medium',     value: attrs.src_lt_utm_medium   || '' },
                    { name: 'lt_campaign',   value: attrs.src_lt_utm_campaign || '' },
                    { name: 'lt_term',       value: attrs.src_lt_utm_term     || '' },
                    { name: 'lt_content',    value: attrs.src_lt_utm_content  || '' },
                ],
            };

            const resp = await fetch(`${BRUSHME_API}/order`, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify(payload),
            });

            const data   = await resp.json();
            const result = data.data || {};

            if (data.success && result.num) {
                await OrderCart.clear();
                // Завжди на сторінку подяки: вона сама дістає замовлення і
                // пропонує оплату, замість кидати покупця просто на платіжну.
                location.href = `/pages/thank-you?order=${encodeURIComponent(result.num)}`;
            } else {
                const S = window.OrderFormStrings || {};
                showResult((S.orderErrorHtml || '').replace('{{ error }}', data.message || ''), 'error');
            }
        } catch (err) {
            console.error(err);
            showResult((window.OrderFormStrings || {}).serverError || '', 'error');
        }
    });
});
